// /client/src/components/CookieView.js
import React, { useEffect } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import CookieContent from './CookieContent';
import PageSEO from './PageSEO'; // Import the SEO component

const CookiePolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // SEO configuration for cookie policy page
  const cookieSEO = {
    title: "Cookie Policy | Kinder's Meats Deli BBQ & Catering",
    description: "Cookie Policy for Kinder's Meats Deli BBQ & Catering. Learn how we use cookies and similar technologies to enhance your experience on our website.",
    keywords: [
      "Kinder's Meats cookie policy",
      "restaurant cookie policy",
      "website cookies",
      "cookie preferences",
      "Google Analytics cookies",
      "website tracking",
      "cookie management",
      "online privacy California"
    ],
    canonicalUrl: "/cookie-policy",
    noIndex: false,
    ogImage: "https://kindersmeats.com/images/kinders-logo-og.jpg",
    customMeta: {
      'document:type': 'Legal Document',
      'document:category': 'Cookie Policy',
      'document:effective-date': 'February 8, 2026',
      'robots': 'index, follow, noarchive'
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/cookie-policy#webpage",
          "url": "https://kindersmeats.com/cookie-policy",
          "name": "Cookie Policy | Kinder's Meats Deli BBQ & Catering",
          "description": "Cookie Policy for Kinder's Meats Deli BBQ & Catering detailing how we use cookies and similar technologies on our website.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "datePublished": "2026-02-08",
          "dateModified": "2026-02-08",
          "inLanguage": "en-US",
          "breadcrumb": {
            "@id": "https://kindersmeats.com/cookie-policy#breadcrumb"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/cookie-policy#cookiepolicy"
          }
        },
        {
          "@type": "DigitalDocument",
          "@id": "https://kindersmeats.com/cookie-policy#cookiepolicy",
          "name": "Kinder's Meats Cookie Policy",
          "description": "Legal document outlining cookie usage and similar tracking technologies on our website",
          "dateCreated": "2026-02-08",
          "dateModified": "2026-02-08",
          "author": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "publisher": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "genre": "Legal Document",
          "keywords": "cookie policy, website cookies, tracking technologies, Google Analytics, cookie management",
          "inLanguage": "en-US",
          "copyrightHolder": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "license": "All rights reserved",
          "about": [
            {
              "@type": "Thing",
              "name": "Cookie Usage",
              "description": "Information about how cookies and similar technologies are used on the website"
            },
            {
              "@type": "Thing",
              "name": "Analytics Tracking",
              "description": "How Google Analytics and performance cookies are used to improve the website"
            },
            {
              "@type": "Thing",
              "name": "Cookie Management",
              "description": "How visitors can manage, disable, or opt out of cookies"
            },
            {
              "@type": "Thing",
              "name": "Third-Party Cookies",
              "description": "Cookies set by third-party services such as ordering platforms"
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization",
          "name": "Kinder's Meats Deli BBQ & Catering",
          "url": "https://kindersmeats.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kindersmeats.com/images/kinders-logo.png",
            "width": 300,
            "height": 100
          },
          "description": "Family-owned BBQ restaurant chain serving authentic barbecue across 7 California locations since 1946.",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "CA",
            "addressCountry": "US"
          },
          "privacyPolicy": "https://kindersmeats.com/privacy-policy",
          "publishingPrinciples": "https://kindersmeats.com/privacy-policy"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/cookie-policy#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Cookie Policy",
              "item": "https://kindersmeats.com/cookie-policy"
            }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What cookies does this website use?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We use essential cookies for site security and basic functionality, and Google Analytics cookies to understand how visitors use our site so we can improve it. We do not use cookies for third-party advertising."
              }
            },
            {
              "@type": "Question",
              "name": "How can I manage or disable cookies?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can control cookies through your browser settings by blocking or deleting them. You can also opt out of Google Analytics specifically by installing Google's browser opt-out add-on. Note that disabling cookies may affect site functionality."
              }
            },
            {
              "@type": "Question",
              "name": "Does this website use cookies for advertising?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No, we do not use cookies to display third-party advertising on our website. We use Google Analytics solely to generate aggregated reports and measure site performance."
              }
            },
            {
              "@type": "Question",
              "name": "Do third-party ordering services set cookies?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, when you click through to Heartland POS for pickup orders or DoorDash for delivery orders, those services may set their own cookies under their own privacy and cookie policies."
              }
            },
            {
              "@type": "Question",
              "name": "What are my cookie rights under California law?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "California privacy law may grant consumers certain rights regarding personal information collected through cookies. If we engage in selling or sharing as defined by California law, we will provide the required opt-out mechanism, including honoring Global Privacy Control signals where applicable."
              }
            }
          ]
        }
      ]
    }
  };

  return (
    <>
      {/* SEO Component */}
      <PageSEO {...cookieSEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-start'>
            <Navigation 
              colorMode="colorWhite"
            />

            {/* Cookie Policy Content */}
            <article itemScope itemType="https://schema.org/DigitalDocument">
              <CookieContent 
                className="content-space-160 is-10-mobile is-10-tablet is-10-desktop is-8-widescreen is-8-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-2-widescreen is-offset-2-fullhd"
                title="Cookie Policy"
                effectiveDate="February 8, 2026"
              />
            </article>

            {/* Hidden FAQ Section for SEO */}
            <section className="faq-section" style={{ display: 'none' }} aria-hidden="true" itemScope itemType="https://schema.org/FAQPage">
              <h2>Cookie Policy FAQ</h2>
              
              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What cookies does this website use?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    We use essential cookies for site security and basic functionality, and Google Analytics cookies to understand how visitors use our site so we can improve it. We do not use cookies for third-party advertising.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">How can I manage or disable cookies?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    You can control cookies through your browser settings by blocking or deleting them. You can also opt out of Google Analytics specifically by installing Google's browser opt-out add-on. Note that disabling cookies may affect site functionality.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Does this website use cookies for advertising?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    No, we do not use cookies to display third-party advertising on our website. We use Google Analytics solely to generate aggregated reports and measure site performance.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Do third-party ordering services set cookies?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Yes, when you click through to Heartland POS for pickup orders or DoorDash for delivery orders, those services may set their own cookies under their own privacy and cookie policies.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What are my cookie rights under California law?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    California privacy law may grant consumers certain rights regarding personal information collected through cookies. If we engage in selling or sharing as defined by California law, we will provide the required opt-out mechanism, including honoring Global Privacy Control signals where applicable.
                  </div>
                </div>
              </div>
            </section>
            
            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default CookiePolicy;